import { useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import type { DirectionMaxRule, EntityTypeUnion, PositionSizeRule } from '@einhaus/project314-types';
import { showToast } from '@components/Layout/Toast';
import Button from '@components/UI/Button';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormSelect from '@components/UI/FormSelect';
import MultiSelect from '@components/UI/MultiSelect';
import {
    directionExposureLabels,
    directionMaxTypeLabels,
    formatRuleValue,
    positionSizeTypeLabels,
    summarizeDirectionRule,
    summarizePositionSizeRule
} from '@/utils/capitalRuleFormatters';

const entityTypeOptions: { value: EntityTypeUnion['entityType'] | 'All'; label: string }[] = [
    { value: 'All', label: 'All entity types' },
    { value: 'Asset', label: 'Stock / option underlying' },
    { value: 'Cryptocurrency', label: 'Cryptocurrency' },
    { value: 'AssetIndex', label: 'Index' }
];

const cloneRules = <T,>(rules: readonly T[]): T[] => structuredClone([...rules]);
const createRuleId = (prefix: string): string => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export interface PositionSizeRulesEditorProps {
    description: string;
    rules: PositionSizeRule[];
    title: string;
    onChange: (rules: PositionSizeRule[]) => void;
}

export const PositionSizeRulesEditor = ({ description, rules, title, onChange }: PositionSizeRulesEditorProps) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
    const [draftRule, setDraftRule] = useState<Partial<PositionSizeRule>>({});

    const openNewRule = () => {
        setEditingRuleId(null);
        setDraftRule({ type: 'fixed', entityTypes: ['Asset'], value: 10000 });
        setIsEditorOpen(true);
    };

    const openExistingRule = (rule: PositionSizeRule) => {
        setEditingRuleId(rule.id);
        setDraftRule(structuredClone(rule));
        setIsEditorOpen(true);
    };

    const saveRule = () => {
        const value = Number(draftRule.value);

        if (!draftRule.entityTypes?.length) {
            showToast('Select at least one entity type', 'error');
            return;
        }

        if (!Number.isFinite(value) || value <= 0) {
            showToast('Position size must be greater than zero', 'error');
            return;
        }

        const rule: PositionSizeRule = {
            id: editingRuleId ?? createRuleId('backtest-size'),
            type: draftRule.type ?? 'fixed',
            entityTypes: [...draftRule.entityTypes],
            value
        };

        onChange(editingRuleId ? rules.map((existingRule) => (existingRule.id === editingRuleId ? rule : existingRule)) : [...rules, rule]);
        setIsEditorOpen(false);
    };

    return (
        <div className="rounded border border-zinc-700 bg-zinc-950/40 p-3">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="text-sm font-medium text-zinc-100">{title}</div>
                    <div className="text-xs text-zinc-400">{description}</div>
                </div>
                <button
                    className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                    type="button"
                    onClick={openNewRule}
                >
                    Add rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div className="rounded bg-zinc-900 px-3 py-2 text-xs text-zinc-400">No position-sizing rules.</div>
            ) : (
                <div className="space-y-2">
                    {rules.map((rule) => (
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded bg-zinc-900 px-3 py-2" key={rule.id}>
                            <div className="text-xs text-zinc-200">
                                <span className="font-medium">{positionSizeTypeLabels[rule.type]}</span>
                                <span className="text-zinc-400">
                                    {' · '}
                                    {rule.type === 'percentage' || rule.type === 'percentagePortfolioRiskCapital' ? '' : '$'}
                                    {formatRuleValue(rule.value)}
                                    {rule.type === 'percentage' || rule.type === 'percentagePortfolioRiskCapital' ? '%' : ''}
                                    {' · '}
                                    {rule.entityTypes.join(', ')}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="text-xs text-blue-300 hover:text-blue-200"
                                    type="button"
                                    onClick={() => openExistingRule(rule)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="text-xs text-red-300 hover:text-red-200"
                                    type="button"
                                    onClick={() => onChange(rules.filter((existingRule) => existingRule.id !== rule.id))}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isEditorOpen && (
                <div className="mt-3 space-y-4 rounded border border-zinc-600 bg-zinc-900 p-3">
                    <div className="text-sm font-medium text-zinc-100">
                        {editingRuleId ? 'Edit position size rule' : 'Add position size rule'}
                    </div>
                    <FormSelect
                        id={`portfolio-backtest-position-size-type-${editingRuleId ?? 'new'}`}
                        label="Rule type"
                        options={Object.entries(positionSizeTypeLabels).map(([value, label]) => ({ value, label }))}
                        value={draftRule.type ?? 'fixed'}
                        onChange={(event) =>
                            setDraftRule((current) => ({ ...current, type: event.target.value as PositionSizeRule['type'] }))
                        }
                    />
                    <div>
                        <label
                            className="mb-2 block text-sm font-medium text-zinc-300"
                            htmlFor={`portfolio-backtest-position-size-entities-${editingRuleId ?? 'new'}`}
                        >
                            Entity types
                        </label>
                        <MultiSelect
                            isMulti
                            className="w-full"
                            id={`portfolio-backtest-position-size-entities-${editingRuleId ?? 'new'}`}
                            options={entityTypeOptions}
                            placeholder="Select entity types"
                            value={(draftRule.entityTypes ?? []).map((entityType) => ({
                                value: entityType,
                                label: entityTypeOptions.find((option) => option.value === entityType)?.label ?? entityType
                            }))}
                            onChange={(selected) => {
                                if (!Array.isArray(selected)) return;
                                setDraftRule((current) => ({
                                    ...current,
                                    entityTypes: selected.map((option) => option.value as EntityTypeUnion['entityType'] | 'All')
                                }));
                            }}
                        />
                    </div>
                    <FormNumberInput
                        id={`portfolio-backtest-position-size-value-${editingRuleId ?? 'new'}`}
                        label="Value"
                        min={0}
                        prefix={draftRule.type === 'percentage' || draftRule.type === 'percentagePortfolioRiskCapital' ? undefined : '$'}
                        suffix={draftRule.type === 'percentage' || draftRule.type === 'percentagePortfolioRiskCapital' ? '%' : undefined}
                        value={draftRule.value === undefined || draftRule.value === '' ? null : Number(draftRule.value)}
                        onValueChange={(value) => setDraftRule((current) => ({ ...current, value: value ?? undefined }))}
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setIsEditorOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={saveRule}>
                            Save rule
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface DirectionMaxRulesEditorProps {
    rules: DirectionMaxRule[];
    onChange: (rules: DirectionMaxRule[]) => void;
}

const DirectionMaxRulesEditor = ({ rules, onChange }: DirectionMaxRulesEditorProps) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
    const [draftRule, setDraftRule] = useState<Partial<DirectionMaxRule>>({});

    const openNewRule = () => {
        setEditingRuleId(null);
        setDraftRule({ direction: 'either', type: 'positions', value: 10 });
        setIsEditorOpen(true);
    };

    const openExistingRule = (rule: DirectionMaxRule) => {
        setEditingRuleId(rule.id);
        setDraftRule(structuredClone(rule));
        setIsEditorOpen(true);
    };

    const saveRule = () => {
        const value = Number(draftRule.value);

        if (!Number.isFinite(value) || value < 0) {
            showToast('Underlying exposure limit cannot be negative', 'error');
            return;
        }

        const rule: DirectionMaxRule = {
            id: editingRuleId ?? createRuleId('backtest-direction-limit'),
            direction: draftRule.direction ?? 'either',
            type: draftRule.type ?? 'positions',
            value
        };

        onChange(editingRuleId ? rules.map((existingRule) => (existingRule.id === editingRuleId ? rule : existingRule)) : [...rules, rule]);
        setIsEditorOpen(false);
    };

    return (
        <div className="rounded border border-zinc-700 bg-zinc-950/40 p-3">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="text-sm font-medium text-zinc-100">Portfolio-wide underlying exposure limits</div>
                    <div className="text-xs text-zinc-400">Shared capacity across every strategy in this backtest.</div>
                </div>
                <button
                    className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                    type="button"
                    onClick={openNewRule}
                >
                    Add rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div className="rounded bg-zinc-900 px-3 py-2 text-xs text-zinc-400">No underlying exposure limit.</div>
            ) : (
                <div className="space-y-2">
                    {rules.map((rule) => (
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded bg-zinc-900 px-3 py-2" key={rule.id}>
                            <div className="text-xs text-zinc-200">
                                <span className="font-medium">{directionExposureLabels[rule.direction]}</span>
                                <span className="text-zinc-400">
                                    {' '}
                                    · {formatRuleValue(rule.value)}
                                    {rule.type === 'percentage' ? '%' : ''} {directionMaxTypeLabels[rule.type]}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="text-xs text-blue-300 hover:text-blue-200"
                                    type="button"
                                    onClick={() => openExistingRule(rule)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="text-xs text-red-300 hover:text-red-200"
                                    type="button"
                                    onClick={() => onChange(rules.filter((existingRule) => existingRule.id !== rule.id))}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isEditorOpen && (
                <div className="mt-3 space-y-4 rounded border border-zinc-600 bg-zinc-900 p-3">
                    <div className="text-sm font-medium text-zinc-100">
                        {editingRuleId ? 'Edit underlying exposure limit' : 'Add underlying exposure limit'}
                    </div>
                    <FormSelect
                        id={`portfolio-backtest-direction-${editingRuleId ?? 'new'}`}
                        label="Underlying exposure"
                        options={[
                            { value: 'either', label: 'Either exposure (separate limits)' },
                            { value: 'long', label: 'Bullish' },
                            { value: 'short', label: 'Bearish' }
                        ]}
                        value={draftRule.direction ?? 'either'}
                        onChange={(event) =>
                            setDraftRule((current) => ({
                                ...current,
                                direction: event.target.value as DirectionMaxRule['direction']
                            }))
                        }
                    />
                    <FormSelect
                        id={`portfolio-backtest-direction-type-${editingRuleId ?? 'new'}`}
                        label="Maximum type"
                        options={[
                            { value: 'positions', label: 'Number of positions' },
                            { value: 'percentage', label: 'Percentage of portfolio' },
                            { value: 'value', label: 'Dollar value' },
                            { value: 'positionCountGap', label: 'Position count gap' }
                        ]}
                        value={draftRule.type ?? 'positions'}
                        onChange={(event) =>
                            setDraftRule((current) => ({
                                ...current,
                                type: event.target.value as DirectionMaxRule['type']
                            }))
                        }
                    />
                    <FormNumberInput
                        integer={draftRule.type === 'positions' || draftRule.type === 'positionCountGap'}
                        id={`portfolio-backtest-direction-value-${editingRuleId ?? 'new'}`}
                        label="Maximum value"
                        min={0}
                        prefix={draftRule.type === 'value' ? '$' : undefined}
                        suffix={draftRule.type === 'percentage' ? '%' : undefined}
                        value={draftRule.value ?? null}
                        onValueChange={(value) => setDraftRule((current) => ({ ...current, value: value ?? undefined }))}
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setIsEditorOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={saveRule}>
                            Save rule
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface PortfolioBacktestCapitalControlsProps {
    directionMaxRules: DirectionMaxRule[];
    positionSizeRules: PositionSizeRule[];
    portfolioDirectionMaxRules: DirectionMaxRule[];
    portfolioPositionSizeRules: PositionSizeRule[];
    onDirectionMaxRulesChange: (rules: DirectionMaxRule[]) => void;
    onPositionSizeRulesChange: (rules: PositionSizeRule[]) => void;
}

const PortfolioBacktestCapitalControls = ({
    directionMaxRules,
    positionSizeRules,
    portfolioDirectionMaxRules,
    portfolioPositionSizeRules,
    onDirectionMaxRulesChange,
    onPositionSizeRulesChange
}: PortfolioBacktestCapitalControlsProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const summary = useMemo(() => {
        const limitSummary =
            directionMaxRules.length === 0 ? 'No position limit' : directionMaxRules.slice(0, 2).map(summarizeDirectionRule).join(', ');
        const sizingSummary =
            positionSizeRules.length === 0 ? 'No default sizing' : positionSizeRules.slice(0, 2).map(summarizePositionSizeRule).join(', ');

        return `${limitSummary} · ${sizingSummary}`;
    }, [directionMaxRules, positionSizeRules]);

    return (
        <div className="rounded-md border border-zinc-600 bg-zinc-800/50">
            <button
                aria-expanded={isExpanded}
                className="flex w-full items-center justify-between gap-4 p-4 text-left"
                type="button"
                onClick={() => setIsExpanded((current) => !current)}
            >
                <div>
                    <div className="font-medium text-white">Capital controls</div>
                    <div className="text-xs text-zinc-400">{summary}</div>
                </div>
                {isExpanded ? (
                    <FaChevronUp aria-hidden="true" className="text-zinc-400" />
                ) : (
                    <FaChevronDown aria-hidden="true" className="text-zinc-400" />
                )}
            </button>

            {isExpanded && (
                <div className="space-y-3 border-t border-zinc-700 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-zinc-400">
                            These values are copied into this backtest only. They do not change the portfolio or live trading.
                        </p>
                        <button
                            className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                            type="button"
                            onClick={() => {
                                onDirectionMaxRulesChange(cloneRules(portfolioDirectionMaxRules));
                                onPositionSizeRulesChange(cloneRules(portfolioPositionSizeRules));
                            }}
                        >
                            Use portfolio defaults
                        </button>
                    </div>

                    <DirectionMaxRulesEditor rules={directionMaxRules} onChange={onDirectionMaxRulesChange} />
                    <PositionSizeRulesEditor
                        description="Used unless a subscribed strategy has its own sizing rules below."
                        rules={positionSizeRules}
                        title="Default position sizing"
                        onChange={onPositionSizeRulesChange}
                    />
                </div>
            )}
        </div>
    );
};

interface StrategyPositionSizeRulesProps {
    defaultRules: PositionSizeRule[];
    rules: PositionSizeRule[] | undefined;
    onChange: (rules: PositionSizeRule[] | undefined) => void;
}

export const StrategyPositionSizeRules = ({ defaultRules, rules, onChange }: StrategyPositionSizeRulesProps) => {
    if (rules === undefined) {
        return (
            <div className="rounded border border-zinc-700 bg-zinc-950/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <div className="text-sm font-medium text-zinc-100">Position sizing</div>
                        <div className="text-xs text-zinc-400">Using the backtest-wide default.</div>
                    </div>
                    <button
                        className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                        type="button"
                        onClick={() => onChange(cloneRules(defaultRules))}
                    >
                        Customize
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex justify-end">
                <button
                    className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                    type="button"
                    onClick={() => onChange(undefined)}
                >
                    Use backtest default
                </button>
            </div>
            <PositionSizeRulesEditor
                description="Applies only to this strategy in this backtest."
                rules={rules}
                title="Strategy position sizing"
                onChange={(updatedRules) => onChange(updatedRules.length > 0 ? updatedRules : undefined)}
            />
        </div>
    );
};

export default PortfolioBacktestCapitalControls;
